import { useContext, useEffect, useState } from "react";
import api from "../api/axios";

import { NavLink } from "react-router-dom";

import {
  Divider,
  Grid,
  Typography,
  Button,
  Chip,
  Box,
  Fade,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";

import Markunread from "@mui/icons-material/Email";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import "./Scss/PanelStyles.scss?v=1.1.5";
import { UserContext } from "../Components/Context/Context";
import {
  dateFormat,
  formatToTenDigits,
  quitarTildes,
} from "../Components/helpers/Helpers";
import { AccessTime, Gavel, Warning } from "@mui/icons-material";

// Componentes propios
import dayjs from "dayjs";
import { CustomIconsPlusText } from "../Components/Common/Design/CustomIconsPlusText";
import LoadingPage from "../Components/Common/Loading/LoadingPage";

const Panel = () => {
  const { userData } = useContext(UserContext);
  const { user } = userData;

  const [pendingAnotherCases, setPendingAnotherCases] = useState([]);
  const [allUserCases, setAllUserCases] = useState([]);
  const [pendingCases, setPendingCases] = useState([]);
  const [allUserCasesMessages, setAllUserCasesMessages] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [flag, setFlag] = useState(false);

  useEffect(() => {
    if (!user?.id) return;

    const getDataPanel = async () => {
      setFlag(false);

      try {
        const { data } = await api.post("/infopanel", {
          user_id: user.id,
        });

        const {
          alerts = [],
          anotherCases = [],
          pendingCases = [],
          allUserCases = [],
          allUserCasesMessages = [],
        } = data || {};

        console.log(data);

        setAllUserCasesMessages(allUserCasesMessages);
        setAllUserCases(allUserCases);
        setPendingCases(pendingCases);
        setPendingAnotherCases(anotherCases);
        setAlerts(alerts);
      } catch (error) {
        console.error("Error al cargar datos del panel:", error);

        setAllUserCasesMessages([]);
        setAllUserCases([]);
        setPendingCases([]);
        setPendingAnotherCases([]);
        setAlerts([]);
      } finally {
        setFlag(true);
      }
    };

    getDataPanel();
  }, [user]);

  /* ===========================
      Renderers por sección
     =========================== */

  const sortedPendingCasesByVencimiento = [...pendingCases].sort((a, b) => {
    const dateA = a?.last_task?.date_end ? dayjs(a.last_task.date_end) : null;

    const dateB = b?.last_task?.date_end ? dayjs(b.last_task.date_end) : null;

    // Casos sin fecha van al final
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;

    // Más próximos (o vencidos) primero
    return dateA.diff(dateB);
  });

  const sortedPendingAnotherCasesByVencimiento = [...pendingAnotherCases].sort(
    (a, b) => {
      const dateA = a?.last_task?.date_end ? dayjs(a.last_task.date_end) : null;

      const dateB = b?.last_task?.date_end ? dayjs(b.last_task.date_end) : null;

      // Casos sin fecha van al final
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;

      // Más próximos (o vencidos) primero
      return dateA.diff(dateB);
    },
  );

  const excludedStateKeywords = ["pendiente", "finalizado"];

  const isExcludedState = (statusName = "") => {
    const normalized = quitarTildes(statusName.toLowerCase());
    return excludedStateKeywords.some((word) => normalized.includes(word));
  };

  const summarizedCases = allUserCases.reduce((acc, item) => {
    const taskStatus = item?.cases_tasks_individual_relation?.status;

    //  Solo status != 0
    if (taskStatus === 0 || taskStatus === undefined) return acc;

    const statusName =
      item?.full_case_status_name ||
      item?.cases_flow_relation?.status_name ||
      "";

    //  Excluir pendientes y finalizados
    if (isExcludedState(statusName)) return acc;

    const caseTypeName = item?.case_type_name || "Sin tipo";

    if (!acc[statusName]) {
      acc[statusName] = {};
    }

    if (!acc[statusName][caseTypeName]) {
      acc[statusName][caseTypeName] = 0;
    }

    acc[statusName][caseTypeName] += 1;

    return acc;
  }, {});

  const renderAlerts = () => {
    if (!alerts.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          No tienes alertas activas.
        </Typography>
      );
    }

    return alerts.map(({ case_id, date, message }, idx) => (
      <Box
        key={idx}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
          p: 1,
          borderRadius: "6px",
          background: "#f9f9f9",
          border: "1px solid #eee",
        }}>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            flexWrap: "wrap",
          }}>
          <Chip label={`#${formatToTenDigits(case_id)}`} size="small" />
          <Chip
            label={date}
            size="small"
            sx={{
              backgroundColor: "#23c6c8",
              color: "white",
            }}
          />
          <Typography
            variant="body2"
            className="myMessage"
            dangerouslySetInnerHTML={{ __html: message }}
          />
        </Box>

        <Button
          size="small"
          component={NavLink}
          to={`/expedientes/ver?id=${case_id}`}
          variant="outlined"
          color="success">
          Ir al Expediente
        </Button>
      </Box>
    ));
  };

  const renderPendingNotification = () => {
    if (!pendingCases.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          No hay cambios recientes en tus expedientes.
        </Typography>
      );
    }

    return (
      <Grid container spacing={1}>
        {sortedPendingCasesByVencimiento.map((item, idx) => (
          <Grid item xs={12} key={idx}>
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                p: 1.5,
                backgroundColor: "#fafafa",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}>
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Expediente #{formatToTenDigits(item.id)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.full_case_status_name}
                  </Typography>
                </Box>

                <Button
                  size="small"
                  component={NavLink}
                  to={`/expedientes/ver?id=${item.id}`}
                  variant="outlined"
                  color="success">
                  Ir al expediente
                </Button>
              </Box>

              <Divider />

              {/* Vencimientos / alertas */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                }}>
                <RenderVencimiento {...item} flagFromPanel={true} />
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderAnotherNotifications = () => {
    if (!pendingAnotherCases.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          No hay cambios recientes en tus expedientes.
        </Typography>
      );
    }

    return (
      <Grid container spacing={1}>
        {sortedPendingAnotherCasesByVencimiento.map((item, idx) => (
          <Grid item xs={12} key={idx}>
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                p: 1.5,
                backgroundColor: "#fafafa",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}>
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Expediente #{formatToTenDigits(item.id)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.full_case_status_name}
                  </Typography>
                </Box>

                <Button
                  size="small"
                  component={NavLink}
                  to={`/expedientes/ver?id=${item.id}`}
                  variant="outlined"
                  color="success">
                  Ir al expediente
                </Button>
              </Box>

              <Divider />

              {/* Vencimientos / alertas */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                }}>
                <RenderVencimiento {...item} flagFromPanel={true} />
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  };

  const getStatusStyles = (statusName = "") => {
    const normalized = quitarTildes(statusName.toLowerCase());

    if (normalized.includes("cancelad")) {
      return { borderColor: "#d32f2f" }; // rojo sobrio
    }

    if (normalized.includes("pendiente")) {
      return { borderColor: "#f9a825" }; // ámbar
    }

    return { borderColor: "#1976d2" }; // azul institucional
  };

  const renderCasesSummary = () => {
    const statusEntries = Object.entries(summarizedCases);

    if (!statusEntries.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          No tienes expedientes en estados avanzados.
        </Typography>
      );
    }

    return (
      <Grid container spacing={2}>
        {statusEntries.map(([statusName, caseTypes], idx) => {
          const styles = getStatusStyles(statusName);

          return (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={idx}
              sx={{
                height: "fit-content",
              }}>
              <Box
                sx={{
                  backgroundColor: "#ffffff",
                  borderRadius: "6px",
                  borderLeft: `4px solid ${styles.borderColor || "#1976d2"}`,
                  boxShadow: `0 2px 4px rgba(0,0,0,0.10), 0 8px 20px rgba(0,0,0,0.14)`,
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "box-shadow 0.2s ease, transform 0.2s ease",
                  "&:hover": {
                    boxShadow: `0 4px 8px rgba(0,0,0,0.14), 0 12px 28px rgba(0,0,0,0.18)`,
                    transform: "translateY(-2px)",
                  },
                }}>
                {/* Header */}
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    color: "text.secondary",
                  }}>
                  Estado
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}>
                  {statusName}
                </Typography>

                <Divider sx={{ mb: 1 }} />

                {/* Body */}
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  {Object.entries(caseTypes).map(([caseTypeName, count], i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.primary" }}>
                        {caseTypeName}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "text.secondary",
                        }}>
                        {count}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const RenderVencimiento = ({
    last_task: current_task,
    cases_flow_relation: flow,
    flagFromPanel = false,
  }) => {
    const fechaFormateada = dateFormat(current_task?.date_end);

    const iconSize = { width: "15px", height: "15px" };

    // Calcular diferencia en días con dayjs
    const diffDays = current_task?.date_end
      ? dayjs(current_task.date_end).diff(dayjs(), "day")
      : null;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: flagFromPanel ? "row" : "column",
          gap: "3px",
          padding: "2px",
        }}>
        {/* Si el estado es juicio */}
        {/* Si la tarea está activa y con fecha */}
        {current_task?.status === 0 && diffDays !== null && (
          <>
            {diffDays > 15 && quitarTildes(flow?.status_name) === "juicio" && (
              <CustomIconsPlusText
                chipStyles={{ width: "auto" }}
                icon={Gavel}
                iconStyles={iconSize}
                text="FECHA DE JUICIO"
                blink="blinking"
                severity="warning"
                variant={"contained"}
              />
            )}
            {diffDays > 0 &&
              diffDays <= 15 &&
              quitarTildes(flow?.status_name) === "juicio" && (
                <CustomIconsPlusText
                  chipStyles={{ width: "auto" }}
                  icon={Gavel}
                  iconStyles={iconSize}
                  text={`JUICIO EN ${diffDays} DÍAS`}
                  blink="blinking"
                  severity="error"
                  variant={"contained"}
                />
              )}
            {diffDays > 5 && (
              <CustomIconsPlusText
                chipStyles={{ width: "auto" }}
                icon={AccessTime}
                iconStyles={iconSize}
                severity="info"
                text={`${diffDays} días para finalizar la tarea`}
                variant={"outlined"}
              />
            )}

            {diffDays > 0 && diffDays <= 5 && (
              <CustomIconsPlusText
                chipStyles={{ width: "auto" }}
                icon={Warning}
                iconStyles={iconSize}
                blink="blinking"
                severity="warning"
                text={`${diffDays} días para finalizar la tarea`}
                variant={"outlined"}
              />
            )}

            {diffDays <= 0 && (
              <CustomIconsPlusText
                chipStyles={{ width: "auto" }}
                icon={Warning}
                iconStyles={iconSize}
                blink="blinking"
                severity="error"
                text={`Tarea vencida hace ${Math.abs(diffDays)} días`}
                variant={"outlined"}
              />
            )}
          </>
        )}

        {/* Fecha de vencimiento */}
        <CustomIconsPlusText
          chipStyles={{ width: "auto" }}
          icon={AccessTime}
          iconStyles={iconSize}
          severity="none"
          text={`Fecha: ${fechaFormateada}`}
          variant={"outlined"}
        />
      </div>
    );
  };

  /* ===========================
      Configuración de secciones
     =========================== */

  const infoSections = [
    {
      title: "Tus Alertas",
      count: alerts?.length,
      subMessage: "Eventos importantes de tus expedientes",
      renderContent: renderAlerts,
    },
    {
      title: "Tus Notificaciones",
      count: pendingCases?.length,
      subMessage: "Acciones que debes realizar",
      renderContent: renderPendingNotification,
    },
    {
      title: "Otras Notificaciones",
      count: pendingAnotherCases?.length,
      subMessage: "Estado de tus expedientes en curso",
      renderContent: renderAnotherNotifications,
    },
  ];

  const panelStats = [
    {
      color: "#23c5c8",
      icon: <Markunread className="panelIcon" />,
      label: "Nuevos emails",
      value: allUserCasesMessages?.length,
    },
    {
      color: "#f8ac59",
      icon: <BusinessCenterIcon className="panelIcon" />,
      label: "Tus expedientes",
      value: allUserCases?.length,
    },
  ];

  return (
    <>
      {!flag && <LoadingPage isLoading={true} />}

      <Fade in={flag} timeout={600}>
        <Grid container item xs={11} className="panelContainer" m="20px auto">
          <Grid item xs={12} mb={1}>
            <Typography variant="h4">
              Bienvenid@, {user?.name} {user?.surname}
            </Typography>
          </Grid>

          <Grid item xs={12} mb={1}>
            <Typography variant="body1">
              Tienes {allUserCasesMessages.length} mensajes sin leer,{" "}
              {pendingCases.length} notificaciones y {alerts.length} alertas
            </Typography>
          </Grid>

          <Divider sx={{ width: "100%", mb: 2 }} />

          {/* Info Sections */}
          <Grid item xs={12}>
            {infoSections.map(
              ({ title, count, subMessage, renderContent }, index) => (
                <Accordion
                  key={index}
                  defaultExpanded={index === 0}
                  sx={{
                    mb: 1,
                    borderRadius: "6px",
                    "&:before": { display: "none" },
                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                  }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box>
                      <Typography variant="h6">
                        {title} ({count})
                      </Typography>
                      {subMessage && (
                        <Typography variant="body2" color="text.secondary">
                          {subMessage}
                        </Typography>
                      )}
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails
                    sx={{
                      ...(count > 3 && {
                        height: "300px",
                        overflow: "auto",
                      }),
                    }}>
                    {renderContent()}
                  </AccordionDetails>
                </Accordion>
              ),
            )}
          </Grid>

          {/* Stats panel */}
          <Grid container justifyContent="center" height={130} mt={2}>
            {panelStats.map(({ color, icon, label, value }, index) => (
              <Grid key={index} item xs={6} p={0.5} sx={{ height: "100%" }}>
                <Grid
                  container
                  sx={{
                    background: `linear-gradient(135deg, ${color} 0%, #ffffffff 100%)`,
                    borderRadius: "4px",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    height: "100%",
                    color: "white",
                  }}>
                  <Grid
                    item
                    xs={4}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 1,
                      height: "100%",
                    }}>
                    {icon}
                  </Grid>
                  <Grid
                    item
                    xs={8}
                    container
                    direction="column"
                    justifyContent="space-between"
                    alignItems="flex-end"
                    p={1}
                    sx={{ height: "100%" }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        lineHeight: 1.1,
                      }}>
                      {label}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "64px",
                        lineHeight: 1,
                        fontWeight: 400,
                        textShadow: "4px 2px 4px gray",
                      }}>
                      {value}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>

          <Grid item xs={12} mt={3}>
            <Typography variant="h6" className="title">
              Tus Expedientes
            </Typography>
            <Grid item xs={12} mt={1}>
              {renderCasesSummary()}
            </Grid>
          </Grid>
        </Grid>
      </Fade>
    </>
  );
};

export default Panel;
